import React, { useState, useEffect } from 'react'
import {
  Box, Card, CardContent, CircularProgress, Grid, Stack, Typography
} from '@mui/material'
import SaveIcon from '@mui/icons-material/Save'
import { formatErrorFieldMessages } from 'interfaces'
import {
  IconButtonWithTooltip,
  cardStyles,
  CommitteeBasicInfoFields,
  useGlobalMessageHandler, useUsRoleTypeLuts, useUsStateLuts
} from 'components'
import { useNavigate } from 'react-router-dom'
import { createCommittee, getComBasicInfo, type CommitteeBasicInformation, type Role } from 'services'
import { getEmptyCommitteeBasicInfo, type CommitteeCreationInfo } from 'helpers'
import { US_COMMITTEE_ROLES } from 'utils'

interface CommitteeCreateCardProps {
  parentId?: number
}

export const CommitteeCreateCard: React.FC<CommitteeCreateCardProps> = ({ parentId }) => {
  const { lutValues: stateLut } = useUsStateLuts()
  const [committeeBasicInfo, setCommitteeBasicInfo] = useState<CommitteeCreationInfo>(getEmptyCommitteeBasicInfo(stateLut.states))
  const [loadingClick, setLoadingClick] = useState(false)
  const [loadingParent, setLoadingParent] = useState(false)
  const { lutValues: roleTypeLutValues } = useUsRoleTypeLuts()
  const [errorFields, setErrorFields] = useState<string[]>([])
  const [parentCommittee, setParentCommittee] = useState<CommitteeBasicInformation | null>(null)
  const navigate = useNavigate()
  const { showSuccessMessage, showErrorMessage, clearMessages } = useGlobalMessageHandler()

  const getChildRoleFromParent = (parentCommittee: CommitteeBasicInformation): Role | null => {
    const { role } = parentCommittee
    if (role?.id == null) return null

    const childRoleMap: Record<number, number> = {
      [US_COMMITTEE_ROLES.FED_COMMITTEE]: US_COMMITTEE_ROLES.FED_SUBCOMMITTEE,
      [US_COMMITTEE_ROLES.STATE_COMMITTEE]: US_COMMITTEE_ROLES.STATE_SUBCOMMITTEE
    }

    const childRoleId = childRoleMap[role.id]

    return childRoleId != null ? roleTypeLutValues.roles.get(childRoleId) ?? null : null
  }

  useEffect(() => {
    if (parentId != null) return
    setCommitteeBasicInfo(prev => prev.state == null ? { ...prev, state: stateLut.states.get(0) ?? null } : prev)
  }, [stateLut.states, parentId])

  useEffect(() => {
    const fetchParentCommittee = async (): Promise<void> => {
      if (parentId != null) {
        try {
          setLoadingParent(true)
          const committee = await getComBasicInfo(parentId)
          setParentCommittee(committee)
          setCommitteeBasicInfo(prev => ({
            ...prev,
            parent: committee,
            role: getChildRoleFromParent(committee),
            chamber: committee.chamber,
            state: committee.state
          }))
        } catch (error) {
          showErrorMessage('Failed to load parent committee information')
          console.error(error)
        } finally {
          setLoadingParent(false)
        }
      }
    }

    void fetchParentCommittee()
  }, [parentId])

  const toggleLoadingClick = (): void => {
    setLoadingClick(prevState => !prevState)
  }

  const handlePostClick = (): void => {
    clearMessages()
    toggleLoadingClick()
    void createCommittee(committeeBasicInfo).then(value => {
      if (value.succeeded && ((value.result?.id) != null)) {
        showSuccessMessage('Committee has been successfully created!')
        navigate(`/us/committee/edit/${value.result.id}`)
      } else {
        showErrorMessage(formatErrorFieldMessages(value.errors))
        if (value.errors != null) {
          setErrorFields(Object.entries(value.errors).map(([key, value]) => key))
        }
      }
    }).catch(() => {
      showErrorMessage('Failed to create Committee')
    }).finally(() => {
      toggleLoadingClick()
    })
  }

  return (
    <Card sx={cardStyles}>
      <CardContent>
        <Typography variant="h6">Create New Committee</Typography>
        <Typography variant="subtitle1">Fill in the required basic information fields for a new committee:</Typography>

        {parentCommittee != null && (
          <Typography variant="subtitle2" sx={{ mt: 1, mb: 2 }}>
            Parent Committee: {parentCommittee.id} - {parentCommittee.name}
          </Typography>
        )}

        {loadingParent
          ? (
          <Box display="flex" justifyContent="center" my={3}>
            <CircularProgress />
          </Box>
            )
          : (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Stack spacing={1}>
                <CommitteeBasicInfoFields
                  loading={loadingClick}
                  committee={committeeBasicInfo}
                  updateValue={setCommitteeBasicInfo}
                  isFieldEmpty={(field: string) => committeeBasicInfo[field as keyof CommitteeCreationInfo] == null || committeeBasicInfo[field as keyof CommitteeCreationInfo] === '' }
                  disabled={false}
                  errorFields={errorFields}
                  showSubs={parentCommittee != null}
                />
              </Stack>
            </Grid>
          </Grid>
            )}

        <Box sx={{ display: 'flex', justifyContent: 'end', mt: 2 }}>
          {loadingClick && <CircularProgress/>}
          <IconButtonWithTooltip
            label="Create Committee"
            color="primary"
            onClick={handlePostClick}
            disabled={loadingClick || loadingParent}
            icon={<SaveIcon />}
          />
        </Box>
      </CardContent>
    </Card>
  )
}
